/**
 * LoopTrack: plays an AudioBuffer once or as a seamless loop, with fade in/out on its own output gain.
 *
 * Loops are stitched with an equal-power crossfade of overlapping segments (`xfade` seconds) instead of
 * `source.loop = true`. That hides MP3 encoder padding and the non-loop-aware endings of generated music/crowd
 * beds. A loop can have an intro: the first pass plays from 0, every repeat restarts at `loopStart`, and the next
 * repeat begins when the playhead reaches `loopEnd` (the material after `loopEnd` is the crossfade tail). Music
 * loop points are found offline so the seam is musically aligned (packages/audio/scripts/build_audio.py).
 *
 * Segments are scheduled ahead on the AudioContext clock by `pump(now)` (the engine calls it ~10×/s), so a
 * throttled timer never causes a gap. Every node is disconnected when its segment ends: no leaks.
 */

/** How far ahead (s) the next loop segment is scheduled. Larger than a background-throttled timer period. */
export const LOOKAHEAD_S = 1.5;

const CURVE_POINTS = 64;
const makeCurve = (rising: boolean): Float32Array => {
  const c = new Float32Array(CURVE_POINTS);
  for (let i = 0; i < CURVE_POINTS; i++) {
    const x = (i / (CURVE_POINTS - 1)) * (Math.PI / 2);
    c[i] = rising ? Math.sin(x) : Math.cos(x);
  }
  return c;
};
/** Equal-power fade curves (shared, read-only). */
export const CURVE_IN = makeCurve(true);
export const CURVE_OUT = makeCurve(false);

interface Segment {
  src: AudioBufferSourceNode;
  gain: GainNode;
}

export interface LoopTrackOptions {
  loop: boolean;
  /** Seam crossfade in seconds (clamped to a quarter of the loop body). Default 1.5. */
  xfade?: number;
  /** Repeat start (s). Default 0. */
  loopStart?: number;
  /** Next repeat begins here (s). Default buffer.duration − xfade. */
  loopEnd?: number;
  /** Playback rate (pitch) of every segment. Default 1. */
  rate?: number;
}

export class LoopTrack {
  readonly out: GainNode;
  private readonly segs: Segment[] = [];
  private nextStart = 0;
  private started = false;
  private stopped = false;
  private stopAt = Infinity;
  private readonly xfade: number;
  private readonly loopStart: number;
  private readonly loopEnd: number;
  private readonly rate: number;

  constructor(
    private readonly ctx: BaseAudioContext,
    readonly id: string,
    private readonly buffer: AudioBuffer,
    dest: AudioNode,
    private readonly opts: LoopTrackOptions,
  ) {
    this.out = ctx.createGain();
    this.out.gain.value = 0;
    this.out.connect(dest);
    const dur = buffer.duration;
    this.rate = opts.rate && opts.rate > 0 ? opts.rate : 1;
    let xf = Math.max(0.005, opts.xfade ?? 1.5);
    const ls = Math.min(Math.max(0, opts.loopStart ?? 0), dur * 0.5);
    // the body (loopStart → loopEnd) must hold at least two crossfades so curves never overlap
    xf = Math.min(xf, (dur - ls) / 4);
    let le = opts.loopEnd ?? dur - xf;
    le = Math.min(Math.max(le, ls + 2 * xf), dur - xf);
    this.xfade = xf;
    this.loopStart = ls;
    this.loopEnd = le;
  }

  /** True once stopped and every segment has ended (or for a one-shot that finished). */
  get done(): boolean {
    return this.started && this.segs.length === 0 && (this.stopped || !this.opts.loop);
  }

  get playing(): boolean {
    return this.started && !this.stopped;
  }

  /** Number of live segments (tests / leak checks). */
  get liveSegments(): number {
    return this.segs.length;
  }

  /** Start at context time `t`, fading the track in to `level` (linear gain, default 1). */
  start(t: number, fadeIn: number, level = 1): void {
    if (this.started) return;
    this.started = true;
    const g = this.out.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(0, t);
    g.linearRampToValueAtTime(level, t + Math.max(0.005, fadeIn));
    this.schedule(t, true);
  }

  /** Schedule the next loop segment when it is due within the lookahead. */
  pump(now: number): void {
    if (!this.started || this.stopped || !this.opts.loop) return;
    // Catch up if the clock ran past (e.g. a very long main-thread stall): never schedule in the past.
    let guard = 0;
    while (this.nextStart - now < LOOKAHEAD_S && this.nextStart < this.stopAt && guard++ < 8) {
      this.schedule(Math.max(this.nextStart, now), false);
    }
  }

  stop(t: number, fadeOut: number): void {
    if (this.stopped) return;
    this.stopped = true;
    if (!this.started) {
      this.out.disconnect();
      return;
    }
    const end = t + Math.max(0.005, fadeOut);
    this.stopAt = end;
    const g = this.out.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(0, end);
    for (const s of this.segs) {
      try {
        s.src.stop(end + 0.02);
      } catch {
        /* already stopped */
      }
    }
    if (this.segs.length === 0) this.out.disconnect();
  }

  /** Immediate teardown (engine dispose). */
  dispose(): void {
    this.stopped = true;
    for (const s of this.segs.splice(0)) {
      s.src.onended = null;
      try {
        s.src.stop();
      } catch {
        /* not started / already stopped */
      }
      s.src.disconnect();
      s.gain.disconnect();
    }
    this.out.disconnect();
  }

  private schedule(t: number, first: boolean): void {
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.buffer;
    src.playbackRate.value = this.rate;
    const gain = ctx.createGain();
    src.connect(gain);
    gain.connect(this.out);
    const xf = this.xfade / this.rate;
    if (this.opts.loop) {
      const offset = first ? 0 : this.loopStart;
      const body = (this.loopEnd - offset) / this.rate;
      // (no setValueAtTime at t for the fade-in: an event inside a curve's span throws in Chrome)
      if (first) gain.gain.setValueAtTime(1, t);
      else gain.gain.setValueCurveAtTime(CURVE_IN, t, xf);
      gain.gain.setValueCurveAtTime(CURVE_OUT, t + body, xf);
      this.nextStart = t + body;
      src.start(t, offset);
      src.stop(t + body + xf + 0.01);
    } else {
      gain.gain.setValueAtTime(1, t);
      src.start(t);
    }
    const seg: Segment = { src, gain };
    src.onended = () => {
      const i = this.segs.indexOf(seg);
      if (i >= 0) this.segs.splice(i, 1);
      src.onended = null;
      src.disconnect();
      gain.disconnect();
      if (this.done) this.out.disconnect();
    };
    this.segs.push(seg);
  }
}
